import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Button, Tabs, Tab, TextField, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimeDisplay from '../components/TimeDisplay';
import ThemeToggle from '../components/ThemeToggle';
import RequestManagementTable from '../components/admin/RequestManagementTable';
import WorkerPerformanceTable from '../components/admin/WorkerPerformanceTable';
import RequesterStatsTable from '../components/admin/RequesterStatsTable';
import RequestForm from '../components/RequestForm';
import TaskCard from '../components/TaskCard';
import InviteCodeGenerator from '../components/admin/InviteCodeGenerator';
import { adminService } from '../services/adminService';
import { Request } from '../types/request';
import { Worker, Requester } from '../types/user';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [tab, setTab] = useState(0);
  const [requestData, setRequestData] = useState<Request[]>([]);
  const [workerData, setWorkerData] = useState<any[]>([]);
  const [requesterData, setRequesterData] = useState<any[]>([]);
  const [workerTasks, setWorkerTasks] = useState<any[]>([]);
  const [taskSearchQuery, setTaskSearchQuery] = useState<string>('');
  const [taskType, setTaskType] = useState<string>('All');
  const [taskSortOption, setTaskSortOption] = useState<string>('Newest First');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      switch (tab) {
        case 0:
          const requests: Request[] = await adminService.getRequests();
          setRequestData(requests);
          break;
        case 1:
          const workers: Worker[] = await adminService.getWorkerStats();
          setWorkerData(workers);
          break;
        case 2:
          const requesters: Requester[] = await adminService.getRequesterStats();
          setRequesterData(requesters);
          break;
        case 4:
          const tasks = await adminService.getWorkerTasks(); 
          setWorkerTasks(tasks);
          break;
      }
    } catch (err) {
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tab]);

  const handleLogout = () => {
    auth.logout();
    navigate('/');
  };

  const filterAndSortTasks = () => {
    let filteredTasks = workerTasks;

    if (taskSearchQuery) {
      filteredTasks = filteredTasks.filter((task) =>
        task.task_id.toLowerCase().includes(taskSearchQuery.toLowerCase())
      );
    }

    if (taskType !== 'All') {
      filteredTasks = filteredTasks.filter(
        (task) => task.document_type === taskType
      );
    }

    if (taskSortOption === 'Newest First') {
      filteredTasks.sort((a, b) =>
        (b.created_at || '').localeCompare(a.created_at || '')
      );
    } else if (taskSortOption === 'Oldest First') {
      filteredTasks.sort((a, b) =>
        (a.created_at || '').localeCompare(b.created_at || '')
      );
    } else if (taskSortOption === 'Document Type (A-Z)') {
      filteredTasks.sort((a, b) =>
        (a.document_type || '').localeCompare(b.document_type || '')
      );
    } else if (taskSortOption === 'Document Type (Z-A)') {
      filteredTasks.sort((a, b) =>
        (b.document_type || '').localeCompare(a.document_type || '')
      );
    }

    return filteredTasks;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Admin Dashboard</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TimeDisplay />
            <ThemeToggle sx={{ mr: 1 }} />
            <Button variant="outlined" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Box>

        <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Request History" />
          <Tab label="Workers" />
          <Tab label="Requesters" />
          <Tab label="Submit Requests" />
          <Tab label="Worker Dashboard" />
          <Tab label="Invite Codes" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <Box sx={{ mb: 2 }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
              variant="contained"
            >
              Refresh Requests
            </Button>
          </Box>
          <RequestManagementTable 
            requests={requestData} 
            loading={loading} 
            onDelete={async (ids) => {
              await adminService.deleteRequests(ids);
              fetchData();
            }}
          />
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Box sx={{ mb: 2 }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
              variant="contained"
            >
              Refresh Workers
            </Button>
          </Box>
          <WorkerPerformanceTable 
            workers={workerData} 
            loading={loading}
            onDeactivate={async (ids) => {
              await adminService.deactivateWorkers(ids);
              fetchData();
            }}
          />
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <Box sx={{ mb: 2 }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
              variant="contained"
            >
              Refresh Requesters
            </Button>
          </Box>
          <RequesterStatsTable 
            requesters={requesterData} 
            loading={loading}
            onDelete={async (ids) => {
              await adminService.deleteRequesters(ids);
              fetchData();
            }}
          />
        </TabPanel>

        <TabPanel value={tab} index={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <RequestForm 
              onSuccess={() => fetchData()}
              setSuccess={(msg) => setError(null)}
              setError={setError}
            />
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={4}>
          <Box sx={{ mb: 2 }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
              variant="contained"
            >
              Refresh Tasks
            </Button>
          </Box>

          {loading && <Typography>Loading tasks...</Typography>}
          {error && <Typography color="error">{error}</Typography>}
          {!loading && !error && workerTasks.length === 0 && (
            <Typography>No tasks available.</Typography>
          )}
          {!loading && workerTasks.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="Search Task ID"
                  value={taskSearchQuery}
                  onChange={(e) => setTaskSearchQuery(e.target.value)}
                  size="small"
                />
                <Select
                  value={taskType}
                  onChange={(e) => setTaskType(e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {[...new Set(workerTasks.map((task) => task.document_type))].map(
                    (type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    )
                  )}
                </Select>
                <Select
                  value={taskSortOption}
                  onChange={(e) => setTaskSortOption(e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="Newest First">Newest First</MenuItem>
                  <MenuItem value="Oldest First">Oldest First</MenuItem>
                  <MenuItem value="Document Type (A-Z)">Document Type (A-Z)</MenuItem>
                  <MenuItem value="Document Type (Z-A)">Document Type (Z-A)</MenuItem>
                </Select>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filterAndSortTasks().map((task) => (
                  <TaskCard
                    key={task.task_id}
                    task={task}
                    onTaskCompleted={() => fetchData()}
                  />
                ))}
              </Box>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tab} index={5}>
          <Box sx={{ mt: 2 }}>
            <InviteCodeGenerator />
          </Box>
        </TabPanel>
      </Box>
    </Container>
  );
};

export default AdminDashboard;
